import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    searchClassesQuerySchema,
    searchClassesResponseSchema,
} from "@common/schemas/class.schema";
import { Class } from "../types/class";

export interface ClassStat {
    id: string;
    // Present-but-nullable: roots have a NULL parentClassId in the DB and
    // serialize as `null` (matching the search row schema convention).
    parentClassId: string | null;
    label: string;
    total: number;
    count: number;
    groupCount: number;
}

const c = initContract();

export const classContract = c.router(
    {
        getClasses: {
            method: "GET",
            path: "/:workspaceId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            responses: {
                200: c.type<{ classes: Class[] }>(),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                403: ErrorMessageSchema,
            },
        },
        searchClasses: {
            method: "GET",
            path: "/:workspaceId/search",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            query: searchClassesQuerySchema,
            responses: {
                200: searchClassesResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                500: ErrorMessageSchema,
            },
        },
        getClassStatistics: {
            path: "/:workspaceId/statistics",
            method: "GET",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            responses: {
                200: c.type<ClassStat[]>(),
            },
            query: z.object({
                entityIds: z.array(z.coerce.number()).optional(),
                start: z.coerce.date().optional(),
                end: z.coerce.date().optional(),
            }),
        },
        getClassTransactionsCount: {
            method: "GET",
            path: "/:workspaceId/transactions-count",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            responses: {
                200: c.type<Record<string, number>>(),
            },
        },
        createClass: {
            method: "POST",
            path: "/:workspaceId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            responses: {
                201: c.type<{ class: Class }>(),
                400: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
            body: z.object({
                label: z.string(),
                parentClassId: z.string().uuid().nullable(),
            }),
        },
        updateClass: {
            method: "PATCH",
            path: "/:workspaceId/:id",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                id: z.string().uuid(),
            }),
            responses: {
                200: c.type<{ class: Class }>(),
                404: ErrorMessageSchema,
            },
            body: z.object({
                label: z.string().optional(),
            }),
        },
        changeClassPosition: {
            method: "POST",
            path: "/:workspaceId/:id/position",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                id: z.string().uuid(),
            }),
            responses: {
                200: c.type<{ class: Class }>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
            body: z.object({
                parentClassId: z.string().uuid().nullable(),
                order: z.number().nonnegative().optional(),
                mergingStrategy: z.enum(["child", "parent"]),
            }),
        },
        deleteClass: {
            method: "DELETE",
            path: "/:workspaceId/:id",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                id: z.string().uuid(),
            }),
            responses: {
                200: z.void(),
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/class",
    },
);
